import { useState, useEffect, useCallback } from 'react';
import { ExternalLink, Calendar, Globe, FolderOpen, Image, Link2, Plus, Pencil, Trash2, X } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const ICON_MAP = {
  calendar: { icon: Calendar, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/15' },
  globe: { icon: Globe, color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/15' },
  folder: { icon: FolderOpen, color: 'text-[#A38560]', bg: 'bg-[#A38560]/10 border-[#A38560]/15' },
  image: { icon: Image, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/15' },
  link: { icon: Link2, color: 'text-white/60', bg: 'bg-white/5 border-white/10' },
};
const ICON_OPTIONS = [
  { value: 'calendar', label: 'Calendario' },
  { value: 'globe', label: 'Plataforma externa' },
  { value: 'folder', label: 'Carpeta / Drive' },
  { value: 'image', label: 'Fotos / Imagenes' },
  { value: 'link', label: 'Link general' },
];
const inputCls = "bg-[#0A1F1A] border-[#1A3B32] text-white placeholder:text-white/30 focus:border-[#A38560] h-11 rounded-xl";

export default function ExternalLinks() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [links, setLinks] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: '', url: '', icon: 'link' });
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    try { setLinks(await api.get('/external-links')); } catch { toast.error('Error cargando links'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim() || !form.url.trim()) return toast.error('Nombre y URL requeridos');
    try {
      if (editId) await api.put(`/external-links/${editId}`, form);
      else await api.post('/external-links', form);
      toast.success('Guardado');
      setModal(false); setEditId(null); setForm({ name: '', url: '', icon: 'link' }); load();
    } catch { toast.error('Error'); }
  };

  const del = async (id) => {
    if (!window.confirm('Eliminar este link?')) return;
    try { await api.delete(`/external-links/${id}`); toast.success('Eliminado'); load(); } catch { toast.error('Error'); }
  };

  return (
    <div className="space-y-6" data-testid="external-links">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-semibold text-white">Links Externos</h1>
          <p className="text-sm text-white/40 mt-1">Recursos y herramientas del equipo</p>
        </div>
        {isAdmin && (
          <button onClick={() => { setForm({ name: '', url: '', icon: 'link' }); setEditId(null); setModal(true); }} data-testid="add-link-btn"
            className="flex items-center gap-2 bg-gradient-to-r from-[#A38560] to-[#8B6F47] text-[#03110D] font-semibold px-4 py-2.5 rounded-xl text-sm">
            <Plus size={16} /> Agregar Link
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {links.map(link => {
          const iconInfo = ICON_MAP[link.icon] || ICON_MAP.link;
          const Icon = iconInfo.icon;
          return (
            <div key={link.id} className="group relative">
              <a href={link.url} target="_blank" rel="noopener noreferrer" data-testid={`link-${link.id}`}
                className={`block rounded-2xl border p-5 transition-all hover:scale-[1.02] hover:shadow-lg ${iconInfo.bg}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-white/5 shrink-0`}>
                    <Icon size={24} className={iconInfo.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white mb-1">{link.name}</p>
                    <p className="text-[10px] text-white/30 truncate">{link.url}</p>
                  </div>
                  <ExternalLink size={14} className="text-white/20 group-hover:text-white/50 shrink-0 mt-1" />
                </div>
              </a>
              {isAdmin && (
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={(e) => { e.preventDefault(); setForm({ name: link.name, url: link.url, icon: link.icon || 'link' }); setEditId(link.id); setModal(true); }}
                    className="p-1.5 rounded-lg bg-[#03110D]/80 hover:bg-white/10 text-white/40 hover:text-white"><Pencil size={12} /></button>
                  <button onClick={(e) => { e.preventDefault(); del(link.id); }}
                    className="p-1.5 rounded-lg bg-[#03110D]/80 hover:bg-red-500/20 text-white/40 hover:text-red-400"><Trash2 size={12} /></button>
                </div>
              )}
            </div>
          );
        })}
        {links.length === 0 && <p className="text-sm text-white/40 col-span-full text-center py-12">No hay links configurados.</p>}
      </div>

      {/* Modal */}
      <Dialog open={modal} onOpenChange={setModal}><DialogContent className="bg-[#0A1F1A] border-[#1A3B32] text-white max-w-md">
        <DialogHeader><DialogTitle className="font-outfit">{editId ? 'Editar' : 'Nuevo'} Link</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-white/50 text-xs uppercase mb-1 block">Nombre</Label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Ej: Calendario de Grabacion" /></div>
          <div><Label className="text-white/50 text-xs uppercase mb-1 block">URL</Label>
            <Input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} className={inputCls} placeholder="https://..." /></div>
          <div><Label className="text-white/50 text-xs uppercase mb-1 block">Icono</Label>
            <Select value={form.icon} onValueChange={v => setForm({ ...form, icon: v })}>
              <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0A1F1A] border-[#1A3B32] text-white">
                {ICON_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select></div>
        </div>
        <DialogFooter><button onClick={save} className="bg-gradient-to-r from-[#A38560] to-[#8B6F47] text-[#03110D] font-semibold px-6 py-2.5 rounded-xl text-sm w-full">Guardar</button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}
